import { Button, ChatThread, getChatCSSVars, LoadingSpinnerLarge, LoadingSpinnerSmall, PendingChangesTray, setContextMenu, SidebarListGrid, StubText, ToolbarNew, useRSInstance, } from '..';
import { DateTimeObj, guid, TW_OVERFLOW_ELLIPSIS, } from '../../../../universal';
import { _, React } from '../../../lib';
import { buildQuestionAnswersMessageText, getPendingChangeStatus, hasPendingApproval, } from './agent-thread-helpers';
export const NEW_THREAD_ID = '__NEW_THREAD__';
const ALL_CHANGES_ID = '__ALL_CHANGES__';
const isCountedChangeStatus = (status) => status === 'pending' || status === 'executing' || status === 'error';
const contentToMessages = (content, completions) => {
    if (content.length === 0) {
        return [];
    }
    const transitionIndices = _.chain(content)
        .map((block, idx) => {
        if (idx === 0) {
            return idx;
        }
        const prevBlock = content[idx - 1];
        return block.role !== prevBlock?.role ||
            (block.role === 'user' && block.timestamp !== prevBlock?.timestamp)
            ? idx
            : -1;
    })
        .filter(idx => idx >= 0)
        .value();
    const messageGroups = transitionIndices.map((startIdx, groupIdx) => {
        const endIdx = transitionIndices[groupIdx + 1] ?? content.length;
        const blocks = content.slice(startIdx, endIdx);
        const role = blocks[0]?.role ?? 'unknown';
        const timestamp = blocks[0]?.timestamp ?? null;
        return { blocks, role, timestamp, groupIdx };
    });
    const sortedCompletions = _.sortBy(completions, c => c.id);
    const getCompletionsForAssistantGroup = (assistantGroupIdx) => {
        const group = messageGroups[assistantGroupIdx];
        if (!group || group.role !== 'assistant') {
            return [];
        }
        const prevUserGroup = _.findLast(messageGroups.slice(0, assistantGroupIdx), g => g.role === 'user');
        const nextUserGroup = _.find(messageGroups.slice(assistantGroupIdx + 1), g => g.role === 'user');
        const turnStart = prevUserGroup?.timestamp
            ? new Date(prevUserGroup.timestamp).getTime()
            : 0;
        const turnEnd = nextUserGroup?.timestamp
            ? new Date(nextUserGroup.timestamp).getTime()
            : Infinity;
        return sortedCompletions.filter(c => {
            const completionTime = new Date(c.requestStart).getTime();
            return completionTime >= turnStart && completionTime < turnEnd;
        });
    };
    return messageGroups.map(({ blocks, role, timestamp, groupIdx }) => {
        const stableID = timestamp ? `${role}-${timestamp}` : `msg-${groupIdx}`;
        const firstBlock = blocks[0];
        const senderId = firstBlock && 'authorUserID' in firstBlock && firstBlock.authorUserID != null
            ? String(firstBlock.authorUserID)
            : null;
        let tokenUsage = null;
        if (role === 'assistant') {
            const turnCompletions = getCompletionsForAssistantGroup(groupIdx);
            if (turnCompletions.length > 0) {
                const inputTokens = _.sumBy(turnCompletions, c => c.tokensRead + c.tokensReadCached);
                const cachedInput = _.sumBy(turnCompletions, c => c.tokensReadCached);
                const outputTokens = _.sumBy(turnCompletions, c => c.tokensWritten);
                const reasoningTokens = _.sumBy(turnCompletions, c => c.tokensReasoning);
                tokenUsage = {
                    inputTokens,
                    outputTokens,
                    totalTokens: inputTokens + outputTokens,
                    cachedInputTokens: cachedInput,
                    reasoningTokens,
                    costCents: _.sumBy(turnCompletions, c => c.costMicrodollars) / 10_000,
                };
            }
        }
        return {
            id: stableID,
            content: blocks,
            timestamp,
            senderID: senderId,
            avatar: null,
            tokenUsage,
        };
    });
};
const formatDateShort = (dateStr) => {
    if (!dateStr) {
        return '';
    }
    const date = DateTimeObj.parse(dateStr);
    if (!date) {
        return '';
    }
    const today = DateTimeObj.now();
    const isSameDay = date.fmt('yyyy-MM-dd') === today.fmt('yyyy-MM-dd');
    const diffDays = today.diffDays(date);
    if (isSameDay) {
        return date.fmt('HH:mm');
    }
    if (diffDays >= 0 && diffDays < 7) {
        return date.fmt('ccc');
    }
    return date.fmt('dd/LL');
};
const formatDateFull = (dateStr) => {
    if (!dateStr) {
        return null;
    }
    const date = DateTimeObj.parse(dateStr);
    if (!date) {
        return null;
    }
    return date.fmt('d LLL yyyy HH:mm');
};
const formatDateGroupTitle = (dateStr) => {
    if (!dateStr) {
        return '';
    }
    const date = DateTimeObj.parse(dateStr);
    if (!date) {
        return '';
    }
    return date.fmt('cccc d LLLL yyyy');
};
const getDefaultState = (props) => ({
    selectedThreadIDs: [NEW_THREAD_ID],
    draftInput: props.seed
        ? { text: props.seed.promptText, attachments: props.seed.attachments }
        : {},
    sblgView: {
        listGridView: {
            groupingField: 'status',
            groupingFieldRev: false,
        },
    },
    allChangesStates: {},
    threadActionLoading: null,
    showApprovedChanges: false,
    chatSessionKey: guid(),
    streamingState: {},
});
export const AgentUI = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        refs: {},
        actionToDelta: null,
    });
    const pendingQuestionSubmitInFlightRef = React.useRef(new Set());
    const contentLoadInFlightRef = React.useRef(new Set());
    const chatThreadRef = React.useRef(null);
    React.useEffect(() => {
        if (props.seed?.isReady) {
            rs.updateState({
                draftInput: {
                    text: props.seed.promptText,
                    attachments: props.seed.attachments,
                },
            });
        }
    }, [props.seed?.attachments, props.seed?.isReady, props.seed?.promptText, rs]);
    const changeHandlers = React.useMemo(() => getChangeHandlers(props), [props]);
    const hideSidebarSelectedValues = props.hideSidebar && _.size(props.threads) === 1
        ? [_.keys(props.threads)[0] ?? NEW_THREAD_ID]
        : rs.state.selectedThreadIDs;
    const allPendingChanges = React.useMemo(() => _.chain(props.threads)
        .flatMap(t => t.pendingChanges)
        .value(), [props.threads]);
    const allPendingChangeThreadIDs = React.useMemo(() => _.chain(props.threads)
        .values()
        .filter(thread => thread.pendingChanges.length > 0)
        .map(thread => thread.metadata.id)
        .value(), [props.threads]);
    React.useEffect(() => {
        if (!props.onLoadThreadContent) {
            return;
        }
        const selectedThreadIDs = hideSidebarSelectedValues.includes(ALL_CHANGES_ID)
            ? allPendingChangeThreadIDs
            : hideSidebarSelectedValues;
        const missingThreadIDs = _.chain(selectedThreadIDs)
            .filter(threadID => threadID !== NEW_THREAD_ID && threadID !== ALL_CHANGES_ID)
            .filter(threadID => !contentLoadInFlightRef.current.has(threadID))
            .filter(threadID => {
            const thread = props.threads[threadID];
            return Boolean(thread && isThreadContentDeferred(thread));
        })
            .value();
        if (missingThreadIDs.length === 0) {
            return;
        }
        missingThreadIDs.forEach(threadID => {
            contentLoadInFlightRef.current.add(threadID);
        });
        props.onLoadThreadContent({
            threadIDs: missingThreadIDs,
            callbacks: {
                onComplete: () => {
                    missingThreadIDs.forEach(threadID => {
                        contentLoadInFlightRef.current.delete(threadID);
                    });
                },
                onError: error => {
                    missingThreadIDs.forEach(threadID => {
                        contentLoadInFlightRef.current.delete(threadID);
                    });
                    console.error('Failed to load agent thread content:', error);
                },
            },
        });
    }, [allPendingChangeThreadIDs, hideSidebarSelectedValues, props]);
    const allChangesRow = allPendingChanges.length > 0
        ? {
            metadata: {
                id: ALL_CHANGES_ID,
                agentType: props.agentType,
                facility: null,
                subFacility: null,
                user: null,
                title: 'All Changes',
                parentTask: null,
                status: 'active',
                createdAt: 'ZZZZ',
                updatedAt: 'ZZZZ',
            },
            firstMessagePreview: null,
            pendingChangesCount: allPendingChanges.length,
            pendingQuestionsCount: 0,
            contentCount: 0,
            completionCount: 0,
            content: [],
            pendingChanges: allPendingChanges,
            pendingQuestions: [],
            completions: [],
        }
        : null;
    const displayData = [
        ...(allChangesRow ? [allChangesRow] : []),
        ..._.values(props.threads),
    ];
    const isThreadStreaming = (threadID) => rs.state.streamingState[threadID]?.isRunning ?? false;
    const getActionableCount = (changes) => _.chain(changes)
        .map(change => rs.state.allChangesStates[change.id] ?? change.status ?? 'pending')
        .filter(isCountedChangeStatus)
        .size()
        .value();
    const threadFields = [
        {
            key: 'status',
            lbl: '',
            width: { init: 24 },
            text: () => '',
            display: thread => {
                if (thread.metadata.id === ALL_CHANGES_ID) {
                    return (React.createElement("img", { alt: "All pending changes", src: "/static/img/i8/color-delta.svg", className: "h-4 w-4 opacity-70 grayscale" }));
                }
                if (isThreadStreaming(thread.metadata.id)) {
                    return React.createElement(LoadingSpinnerSmall, null);
                }
                if (thread.metadata.user != null) {
                    return (React.createElement("img", { alt: "Private (only you can see this)", src: "/static/img/i8/color-security-lock.svg", className: "h-4 w-4 opacity-70 grayscale" }));
                }
                return React.createElement(React.Fragment, null);
            },
            groupVal: thread => {
                if (thread.metadata.id === ALL_CHANGES_ID) {
                    return 'all-changes';
                }
                if (isThreadStreaming(thread.metadata.id)) {
                    return 'processing';
                }
                return thread.metadata.user != null ? 'private' : 'shared';
            },
            groupName: groupID => {
                switch (groupID) {
                    case 'processing':
                        return 'Processing';
                    case 'private':
                        return 'Private Threads';
                    case 'shared':
                        return 'Shared Threads';
                    case 'all-changes':
                        return 'All Changes';
                    default:
                        return '';
                }
            },
            groupSort: group => {
                switch (group.ID) {
                    case 'all-changes':
                        return 0;
                    case 'processing':
                        return 1;
                    case 'private':
                        return 2;
                    case 'shared':
                        return 3;
                    default:
                        return 4;
                }
            },
            sortVal: thread => {
                if (thread.metadata.id === ALL_CHANGES_ID) {
                    return -1;
                }
                if (isThreadStreaming(thread.metadata.id)) {
                    return 0;
                }
                if (thread.metadata.status === 'archived') {
                    return 2;
                }
                return thread.metadata.user != null ? 0 : 1;
            },
        },
        {
            key: 'updatedAt',
            lbl: 'Date',
            width: { init: 50 },
            text: thread => thread.metadata.id === ALL_CHANGES_ID
                ? ''
                : formatDateShort(thread.metadata.updatedAt),
            tooltip: thread => thread.metadata.id === ALL_CHANGES_ID
                ? null
                : formatDateFull(thread.metadata.updatedAt),
            groupVal: thread => {
                if (thread.metadata.id === ALL_CHANGES_ID) {
                    return 'all-changes';
                }
                const updatedAt = DateTimeObj.parse(thread.metadata.updatedAt);
                return updatedAt ? updatedAt.fmt('yyyy-MM-dd') : 'unknown';
            },
            groupName: (_groupID, record) => record.metadata.id === ALL_CHANGES_ID
                ? 'All Changes'
                : formatDateGroupTitle(record.metadata.updatedAt),
            groupSort: group => {
                if (group.ID === 'all-changes') {
                    return -1;
                }
                const updatedAt = DateTimeObj.parse(group.Records[0]?.metadata.updatedAt ?? '');
                return updatedAt?.u ?? 0;
            },
            sortVal: thread => thread.metadata.updatedAt,
            sortReverseDefault: true,
        },
        {
            key: 'title',
            lbl: 'Thread',
            text: thread => thread.metadata.title ?? thread.firstMessagePreview ?? 'New conversation',
            className: () => TW_OVERFLOW_ELLIPSIS,
            sortVal: thread => thread.metadata.title ?? thread.firstMessagePreview ?? '',
        },
        {
            key: 'pendingChanges',
            lbl: 'Δ',
            lblDesc: 'Pending changes',
            width: { init: 30 },
            className: () => 'text-right',
            text: thread => {
                const actionableCount = thread.pendingChanges.length > 0
                    ? getActionableCount(thread.pendingChanges)
                    : thread.pendingChangesCount;
                return actionableCount > 0 ? String(actionableCount) : '';
            },
            sortVal: thread => thread.pendingChanges.length > 0
                ? getActionableCount(thread.pendingChanges)
                : thread.pendingChangesCount,
        },
    ];
    if (props.hideSidebar) {
        return (React.createElement(AgentUIMainPane, { rs: rs, pendingQuestionSubmitInFlightRef: pendingQuestionSubmitInFlightRef, selectedValues: hideSidebarSelectedValues, allPendingChanges: allPendingChanges, changeHandlers: changeHandlers, chatThreadRef: chatThreadRef }));
    }
    return (React.createElement(SidebarListGrid, { pk: thread => thread.metadata.id, data: displayData, fields: threadFields, buildForm: selectedValues => (React.createElement(AgentUIMainPane, { rs: rs, pendingQuestionSubmitInFlightRef: pendingQuestionSubmitInFlightRef, selectedValues: selectedValues, allPendingChanges: allPendingChanges, changeHandlers: changeHandlers, chatThreadRef: chatThreadRef })), multiple: true, value: rs.state.selectedThreadIDs, onUpdateValue: value => {
            const newSelection = value.length > 0 ? value : [NEW_THREAD_ID];
            rs.updateState(state => {
                const oldSingle = state.selectedThreadIDs.length === 1
                    ? state.selectedThreadIDs[0]
                    : null;
                const newSingle = newSelection.length === 1 ? newSelection[0] : null;
                const needsNewKey = oldSingle !== newSingle;
                return {
                    selectedThreadIDs: newSelection,
                    chatSessionKey: needsNewKey ? guid() : state.chatSessionKey,
                };
            });
        }, view: rs.state.sblgView, onUpdateView: view => {
            rs.updateState(state => ({ sblgView: { ...state.sblgView, ...view } }));
        }, sidebarWidth: {
            defaultWidth: 280,
            minWidth: 180,
            maxWidth: 600,
        }, listGridSettings: {
            defaultSortingKey: 'updatedAt',
            defaultSortingReverse: true,
            onContextMenu: (record, event) => {
                handleContextMenu(rs, record, event);
            },
        }, stubMessage: { lbl: 'Select a thread or start a new one' }, adding: {
            onClick: () => {
                rs.updateState({ selectedThreadIDs: [NEW_THREAD_ID] });
                setTimeout(() => chatThreadRef.current?.focusTextarea(), 0);
            },
        }, inactivity: {
            lbl: { text: 'Archived', width: 85 },
            isInactive: thread => thread.metadata.status === 'archived',
            className: 'text-neutral-500',
        } }));
};
const AgentUIMainPane = (props) => {
    const { rs, selectedValues, allPendingChanges, changeHandlers, chatThreadRef } = props;
    const agentProps = rs.props;
    const isDarkMode = (agentProps.theme ?? 'light') === 'dark';
    if (selectedValues.length >= 1) {
        const selectedValue = selectedValues[0] ?? null;
        const isMulti = selectedValues.length > 1;
        const isAllChanges = selectedValue === ALL_CHANGES_ID;
        const isNewThread = selectedValue === NEW_THREAD_ID;
        const selectedThreadID = !isMulti && !isAllChanges && !isNewThread ? selectedValue : null;
        const realThreadIDs = selectedValues.filter(id => id !== NEW_THREAD_ID && id !== ALL_CHANGES_ID);
        const selectedChanges = isAllChanges
            ? allPendingChanges
            : _.chain(agentProps.threads)
                .pick(realThreadIDs)
                .flatMap(thread => thread.pendingChanges)
                .value();
        const changesWithStatus = selectedChanges.map(change => ({
            ...change,
            status: rs.state.allChangesStates[change.id] ?? getPendingChangeStatus(change),
            createdAt: '',
        }));
        const actionableChangesCount = changesWithStatus.filter(change => change.status === 'pending' ||
            change.status === 'executing' ||
            change.status === 'error').length;
        const executingIDs = new Set(changesWithStatus
            .filter(change => change.status === 'executing')
            .map(change => change.id));
        const allThreadFileLookup = getAgentThreadFileLookup(agentProps.threads);
        const allChangesToolHandlers = {
            ...(agentProps.toolHandlers ?? {}),
            getThreadFile: (fileID) => allThreadFileLookup[fileID] ?? null,
        };
        const updateChangeStates = (updates) => {
            rs.updateState(state => ({
                allChangesStates: { ...state.allChangesStates, ...updates },
            }));
        };
        const selectedThreads = _.chain(realThreadIDs)
            .map(id => agentProps.threads[id])
            .compact()
            .value();
        const hasArchivedThreads = selectedThreads.some(thread => thread.metadata.status === 'archived');
        const hasActiveThreads = selectedThreads.some(thread => thread.metadata.status === 'active');
        const hasSharedThreads = selectedThreads.some(thread => thread.metadata.user == null);
        const hasPrivateThreads = selectedThreads.some(thread => thread.metadata.user != null);
        const canMakePrivate = hasSharedThreads &&
            selectedThreads.length > 0 &&
            agentProps.currentUserID != null;
        const handleAction = (action) => {
            const onThreadAction = agentProps.onThreadAction;
            if (!onThreadAction || rs.state.threadActionLoading != null) {
                return;
            }
            rs.updateState({ threadActionLoading: action });
            Promise.resolve()
                .then(() => {
                onThreadAction({
                    threadIDs: realThreadIDs,
                    action,
                    callbacks: {
                        onComplete: () => {
                            rs.updateState({ threadActionLoading: null });
                            if (action === 'archive' || action === 'unarchive') {
                                rs.updateState({ selectedThreadIDs: [NEW_THREAD_ID] });
                            }
                        },
                        onError: error => {
                            rs.updateState({ threadActionLoading: null });
                            console.error(`Failed to ${action} threads:`, error);
                        },
                    },
                });
            })
                .catch(error => {
                rs.updateState({ threadActionLoading: null });
                console.error(`Failed to ${action} threads:`, error);
            });
        };
        const handleApprove = async (changeIDs) => {
            const previousStatuses = _.chain(changesWithStatus)
                .filter(change => changeIDs.includes(change.id))
                .map(change => [change.id, change.status])
                .fromPairs()
                .value();
            const approvalsToExecute = _.chain(changesWithStatus)
                .filter(change => changeIDs.includes(change.id))
                .filter(change => change.status === 'pending' || change.status === 'error')
                .value();
            if (approvalsToExecute.length === 0) {
                return;
            }
            updateChangeStates(_.chain(approvalsToExecute)
                .map(change => [change.id, 'executing'])
                .fromPairs()
                .value());
            const threadsToSearch = isAllChanges
                ? _.values(agentProps.threads)
                : _.chain(agentProps.threads).pick(realThreadIDs).values().value();
            const approvalsWithThreads = _.chain(approvalsToExecute)
                .map(change => {
                const thread = threadsToSearch.find(candidate => candidate.pendingChanges.some((pendingChange) => pendingChange.id === change.id));
                return thread
                    ? { approvalID: change.id, threadID: thread.metadata.id }
                    : null;
            })
                .compact()
                .value();
            const approvalIDs = approvalsWithThreads.map(change => change.approvalID);
            const missingIDs = approvalsToExecute
                .map(change => change.id)
                .filter(approvalID => !approvalIDs.includes(approvalID));
            if (missingIDs.length > 0) {
                updateChangeStates(_.pick(previousStatuses, missingIDs));
            }
            if (approvalIDs.length === 0) {
                return;
            }
            try {
                const result = await changeHandlers.onExecuteApproved({
                    threadIDs: _.chain(approvalsWithThreads)
                        .map(change => change.threadID)
                        .uniq()
                        .value(),
                    approvalIDs,
                });
                const errorIDs = (result.errors ?? []).map(error => error.approvalID);
                updateChangeStates(_.chain(approvalIDs)
                    .map(approvalID => [
                    approvalID,
                    errorIDs.includes(approvalID) ? 'error' : 'approved',
                ])
                    .fromPairs()
                    .value());
            }
            catch {
                updateChangeStates(_.chain(approvalIDs)
                    .map(approvalID => [approvalID, 'error'])
                    .fromPairs()
                    .value());
            }
        };
        const handleReject = (changeIDs) => {
            updateChangeStates(_.chain(changeIDs)
                .map(changeID => [changeID, 'rejected'])
                .fromPairs()
                .value());
            agentProps.onRejectChanges({
                approvalIDs: changeIDs,
                callbacks: {
                    onComplete: threads => {
                        updateChangeStates(_.chain(changeIDs)
                            .map(changeID => [
                            changeID,
                            hasPendingApproval(threads, changeID)
                                ? 'pending'
                                : 'rejected',
                        ])
                            .fromPairs()
                            .value());
                    },
                },
            });
        };
        const toolbar = (React.createElement("div", { className: "sticky top-0 z-20 bg-white border-b border-neutral-300", style: { height: '40px' } },
            React.createElement(ToolbarNew, { lhs: [
                    {
                        type: 'label',
                        width: 130,
                        importance: 100,
                        lbl: `${actionableChangesCount} pending change${actionableChangesCount !== 1 ? 's' : ''}`,
                        className: 'px-1 text-black/80',
                    },
                ], rhs: [
                    {
                        type: 'checkbox',
                        width: 170,
                        importance: 11,
                        lbl: 'Show approved changes',
                        alwaysCollapse: true,
                        overflowVisible: true,
                        value: rs.state.showApprovedChanges,
                        onUpdate: value => {
                            rs.updateState({ showApprovedChanges: value });
                        },
                    },
                    {
                        type: 'button',
                        width: 24,
                        alwaysCollapse: true,
                        overflowVisible: true,
                        lbl: 'Copy thread ID',
                        visible: Boolean(selectedThreadID),
                        disabled: !selectedThreadID,
                        onClick: () => {
                            if (!selectedThreadID || !navigator.clipboard) {
                                return;
                            }
                            navigator.clipboard
                                .writeText(selectedThreadID)
                                .catch(console.error);
                        },
                    },
                    {
                        type: 'button',
                        width: 116,
                        importance: 10,
                        img: '/static/img/i8/ios16-refresh.svg',
                        lbl: 'Unarchive',
                        disabled: rs.state.threadActionLoading != null,
                        onClick: () => {
                            handleAction('unarchive');
                        },
                        title: 'Restore archived threads',
                        visible: agentProps.onThreadAction != null && hasArchivedThreads,
                    },
                    {
                        type: 'button',
                        width: 98,
                        importance: 10,
                        img: '/static/img/i8/color-archive.svg',
                        lbl: 'Archive',
                        disabled: rs.state.threadActionLoading != null,
                        onClick: () => {
                            handleAction('archive');
                        },
                        title: 'Archive selected threads',
                        visible: agentProps.onThreadAction != null && hasActiveThreads,
                    },
                    {
                        type: 'button',
                        width: 84,
                        importance: 9,
                        img: '/static/img/i8/color-collaboration.svg',
                        lbl: 'Share',
                        disabled: rs.state.threadActionLoading != null,
                        onClick: () => {
                            handleAction('share');
                        },
                        title: 'Share with all users',
                        visible: agentProps.onThreadAction != null && hasPrivateThreads,
                    },
                    {
                        type: 'button',
                        width: 136,
                        importance: 9,
                        img: '/static/img/i8/color-security-lock.svg',
                        lbl: 'Make Private',
                        disabled: rs.state.threadActionLoading != null,
                        onClick: () => {
                            handleAction('make-private');
                        },
                        title: 'Make private',
                        visible: agentProps.onThreadAction != null &&
                            hasSharedThreads &&
                            canMakePrivate,
                    },
                ] })));
        let body = React.createElement(React.Fragment, null);
        if (isMulti || isAllChanges) {
            body = (React.createElement("div", { className: "flex-1 relative min-h-0" },
                React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                        backgroundColor: isDarkMode
                            ? 'oklch(0 0 0 / 0.9)'
                            : 'oklch(0 0 0 / 0.1)',
                    } }),
                React.createElement("div", { className: "absolute inset-0 pointer-events-none z-0", style: {
                        backgroundImage: 'url(/static/img/svg/window-background-texture.svg)',
                        backgroundRepeat: 'repeat',
                        backgroundSize: '5px',
                        filter: isDarkMode
                            ? 'hue-rotate(200deg) invert(1) brightness(0.9)'
                            : 'hue-rotate(260deg) grayscale(50%)',
                        opacity: isDarkMode ? 0.15 : 0.2,
                    } }),
                React.createElement("div", { className: "absolute inset-0 overflow-y-scroll z-10 flex flex-col px-1" },
                    React.createElement("div", { className: "w-full pt-4 pb-4 px-1" }, changesWithStatus.length > 0 ? (React.createElement(PendingChangesTray, { changes: changesWithStatus, renderers: agentProps.changeRenderers, toolHandlers: allChangesToolHandlers, onApprove: ids => {
                            handleApprove(ids).catch(console.error);
                        }, onReject: handleReject, executingIDs: executingIDs, disabled: false, widgetToGroupCode: agentProps.widgetToGroupCode, showApprovedChanges: rs.state.showApprovedChanges })) : (React.createElement("div", { className: "flex items-center justify-center py-8" },
                        React.createElement("p", { className: "text-sm text-neutral-500" }, "No pending changes in selected threads")))))));
        }
        else if (isNewThread) {
            const contextReadTokens = getLatestContextReadTokens(rs, null);
            body =
                agentProps.seed && !agentProps.seed.isReady ? (React.createElement("div", { className: "p-3 text-sm text-neutral-600 flex-1" },
                    React.createElement(StubText, null, agentProps.seed.preparingText ?? 'Preparing...'))) : (React.createElement("div", { className: "flex-1 min-h-0 relative" },
                    React.createElement(ChatThread, { ref: chatThreadRef, key: rs.state.chatSessionKey, originalMessageState: "", originalInputState: rs.state.draftInput, autoSendOnMount: Boolean(agentProps.seed != null &&
                            agentProps.seed.isReady &&
                            hasDraftInputContent(rs.state.draftInput)), messages: [], hideUserAvatar: true, hue: agentProps.hue ?? 145, placeholder: agentProps.placeholder ?? 'Send a message…', theme: agentProps.theme ?? 'light', thoughtAccess: agentProps.thoughtAccess ?? 'full', change: changeHandlers, sending: null, controlled: makeControlledProps(rs, null, props.pendingQuestionSubmitInFlightRef), currentUserID: agentProps.currentUserID != null
                            ? String(agentProps.currentUserID)
                            : undefined, isReadOnly: agentProps.isReadOnly, readOnlyMessage: agentProps.readOnlyMessage, showApprovedChanges: rs.state.showApprovedChanges, contextReadTokens: contextReadTokens, toolRenderers: agentProps.toolRenderers, toolHandlers: agentProps.toolHandlers })));
        }
        else {
            const thread = agentProps.threads[selectedValue ?? ''];
            const streamState = rs.state.streamingState[selectedValue ?? ''];
            const contextReadTokens = getLatestContextReadTokens(rs, selectedValue ?? null);
            const controlled = makeControlledProps(rs, selectedValue ?? null, props.pendingQuestionSubmitInFlightRef);
            const isLoadingDeferredContent = Boolean(thread && isThreadContentDeferred(thread) && !streamState?.isRunning);
            const lastMessage = controlled.messages[controlled.messages.length - 1];
            const canRetryLastUserMessage = !controlled.isRunning &&
                controlled.messages.length > 0 &&
                lastMessage?.content[0]?.role === 'user';
            body =
                !thread && !streamState ? (React.createElement("div", { className: "p-3 flex-1" },
                    React.createElement(StubText, null, "Thread not found"))) : (React.createElement("div", { className: "flex-1 min-h-0 relative" },
                    React.createElement(ChatThread, { ref: chatThreadRef, key: rs.state.chatSessionKey, originalMessageState: "", messages: [], hideUserAvatar: true, hue: agentProps.hue ?? 145, placeholder: "Continue conversation\u2026", theme: agentProps.theme ?? 'light', thoughtAccess: agentProps.thoughtAccess ?? 'full', change: changeHandlers, sending: null, controlled: controlled, messagesSlot: isLoadingDeferredContent ? (React.createElement("div", { className: "w-full h-full relative" },
                            React.createElement(LoadingSpinnerLarge, null))) : undefined, hideTrays: isLoadingDeferredContent, textboxSlotAbove: canRetryLastUserMessage ? (React.createElement("div", { className: "flex justify-center" },
                            React.createElement(Button, { type: "submit", lbl: "Try Again", className: "mx-0 px-3 py-1.5", classNameSpan: "text-sm", onClick: () => {
                                    if (!lastMessage) {
                                        return;
                                    }
                                    controlled.onRetryLastRun?.();
                                } }))) : undefined, currentUserID: agentProps.currentUserID != null
                            ? String(agentProps.currentUserID)
                            : undefined, isReadOnly: agentProps.isReadOnly, readOnlyMessage: agentProps.readOnlyMessage, showApprovedChanges: rs.state.showApprovedChanges, contextReadTokens: contextReadTokens, toolRenderers: agentProps.toolRenderers, toolHandlers: agentProps.toolHandlers })));
        }
        return (React.createElement("div", { className: "tailwind-wrapper h-full flex flex-col bg-[oklch(var(--chat-l-bg)_0_0)]", style: {
                ...getChatCSSVars(agentProps.hue ?? 145, isDarkMode),
                fontFamily: 'var(--font-condensed)',
            } },
            toolbar,
            body));
    }
    return null;
};
const getAgentThreadFileLookup = (threads) => _.chain(threads)
    .values()
    .flatMap(thread => thread.content)
    .flatMap(block => {
    if (block.type === 'attachment-manifest') {
        return block.files.map(file => [
            file.id,
            { mimeType: file.mimeType, size: file.size },
        ]);
    }
    if (block.type === 'file' && block.file.id != null) {
        return [
            [
                `thread:${block.file.id}`,
                {
                    mimeType: block.file.mimeType,
                    size: block.file.size,
                },
            ],
        ];
    }
    return [];
})
    .fromPairs()
    .value();
const getLatestContextReadTokens = (rs, threadID) => {
    const stateKey = threadID ?? NEW_THREAD_ID;
    const streamState = rs.state.streamingState[stateKey];
    if (streamState?.latestContextReadTokens != null) {
        return streamState.latestContextReadTokens;
    }
    if (!threadID) {
        return null;
    }
    const thread = rs.props.threads[threadID];
    const latestCompletion = thread && (thread.completions?.length ?? 0) > 0
        ? thread.completions[thread.completions.length - 1]
        : null;
    if (!latestCompletion) {
        return null;
    }
    return latestCompletion.tokensRead + latestCompletion.tokensReadCached;
};
const isThreadContentDeferred = (thread) => (thread.content?.length ?? 0) < thread.contentCount ||
    (thread.pendingQuestions?.length ?? 0) < thread.pendingQuestionsCount ||
    (thread.completions?.length ?? 0) < thread.completionCount;
const hasDraftInputContent = (input) => {
    const text = input.text?.trim() ?? '';
    const attachments = input.attachments ?? [];
    return text.length > 0 || attachments.length > 0;
};
const mergeByID = (existing, incoming) => [
    ...existing.map(item => _.find(incoming, next => next.id === item.id) ?? item),
    ...incoming.filter(item => !existing.some(existingItem => existingItem.id === item.id)),
];
const appendAssistantErrorMessage = (messages, error) => {
    const timestamp = new Date().toISOString();
    return [
        ...messages,
        {
            id: guid(),
            content: [
                {
                    role: 'assistant',
                    timestamp,
                    type: 'error',
                    error,
                },
            ],
            timestamp,
            avatar: null,
        },
    ];
};
const getContextTokenUpdate = (state, inputTokens) => {
    if (inputTokens == null || inputTokens === state.lastUsageInputTokens) {
        return {
            latestContextReadTokens: state.latestContextReadTokens,
            lastUsageInputTokens: state.lastUsageInputTokens,
        };
    }
    return {
        latestContextReadTokens: state.lastUsageInputTokens == null || inputTokens < state.lastUsageInputTokens
            ? inputTokens
            : inputTokens - state.lastUsageInputTokens,
        lastUsageInputTokens: inputTokens,
    };
};
const buildRetryPayload = (message) => {
    if ((message.content[0]?.role ?? 'assistant') !== 'user') {
        return null;
    }
    const retryTimestamp = new Date().toISOString();
    const retryContent = message.content.map(block => ({
        ...block,
        role: 'user',
        timestamp: retryTimestamp,
    }));
    return {
        content: retryContent,
        userMessage: {
            id: guid(),
            content: retryContent,
            timestamp: retryTimestamp,
            avatar: message.avatar,
            senderID: message.senderID,
        },
        reuseExistingUserMessage: true,
    };
};
const makeControlledProps = (rs, threadID, pendingQuestionSubmitInFlightRef) => {
    const props = rs.props;
    const deleteMessage = props.onDeleteMessage;
    const stateKey = threadID ?? NEW_THREAD_ID;
    const streamState = getStreamingState(rs, stateKey);
    const thread = threadID ? props.threads[threadID] : null;
    const serverMessages = thread
        ? contentToMessages(thread.content, thread.completions)
        : [];
    const effectiveMessages = streamState.localMessages.length > 0 ? streamState.localMessages : serverMessages;
    const pendingChangesCreatedAt = thread?.metadata.updatedAt ??
        thread?.metadata.createdAt ??
        DateTimeObj.now().fmt();
    const serverChanges = (thread?.pendingChanges ?? []).map(change => ({
        ...change,
        status: change.status ?? getPendingChangeStatus(change),
        createdAt: pendingChangesCreatedAt,
    }));
    const effectivePendingChanges = streamState.isRunning || streamState.localPendingChanges.length > 0
        ? streamState.localPendingChanges
        : serverChanges;
    const serverQuestions = (thread?.pendingQuestions ?? []).map((c) => ({
        ...c,
        createdAt: pendingChangesCreatedAt,
    }));
    const effectivePendingQuestions = streamState.isRunning || streamState.localPendingQuestions.length > 0
        ? streamState.localPendingQuestions
        : serverQuestions;
    const deleteMessageFromServer = threadID && deleteMessage
        ? (message) => new Promise(resolve => {
            const messageTimestamp = message.timestamp ?? message.content[0]?.timestamp;
            if (!messageTimestamp) {
                resolve(false);
                return;
            }
            let previousMessages = [];
            updateStreamingState(rs, stateKey, state => {
                previousMessages =
                    state.localMessages.length > 0
                        ? state.localMessages
                        : effectiveMessages;
                return {
                    localMessages: previousMessages.filter(m => m.id !== message.id),
                };
            });
            deleteMessage({
                threadID,
                isAgent: (message.content[0]?.role ?? 'assistant') === 'assistant',
                timestamp: messageTimestamp,
                callbacks: {
                    onComplete: () => {
                        resolve(true);
                    },
                    onError: error => {
                        updateStreamingState(rs, stateKey, () => ({
                            localMessages: previousMessages,
                        }));
                        console.error('Failed to delete message:', error);
                        resolve(false);
                    },
                },
            });
        })
        : undefined;
    const retryExistingUserMessage = (message) => {
        const retryPayload = buildRetryPayload(message);
        if (!retryPayload) {
            return;
        }
        const messages = streamState.localMessages.length > 0
            ? streamState.localMessages
            : effectiveMessages;
        const messageIndex = messages.findIndex(m => m.id === message.id);
        const messagesToDelete = messageIndex < 0 ? [] : messages.slice(messageIndex + 1);
        Promise.resolve()
            .then(() => messagesToDelete.reduce((promise, laterMessage) => promise.then(async (shouldContinue) => shouldContinue && deleteMessageFromServer
            ? deleteMessageFromServer(laterMessage)
            : shouldContinue), Promise.resolve(true)))
            .then(deletedOK => {
            if (!deletedOK) {
                console.error('Failed to delete later messages before retry');
                return;
            }
            requestAnimationFrame(() => {
                makeControlledProps(rs, threadID, pendingQuestionSubmitInFlightRef).onSend(retryPayload);
            });
        });
    };
    return {
        messages: effectiveMessages,
        pendingMessage: streamState.pendingMessage,
        isRunning: streamState.isRunning,
        threadID,
        pendingChanges: effectivePendingChanges,
        pendingQuestions: effectivePendingQuestions,
        onSend: input => {
            const isNewThread = !threadID;
            const trackedThreadID = threadID ?? guid();
            const shouldPersistMessage = !input.reuseExistingUserMessage;
            rs.updateState(state => ({
                selectedThreadIDs: [trackedThreadID],
                draftInput: isNewThread ? {} : state.draftInput,
                streamingState: {
                    ...state.streamingState,
                    [trackedThreadID]: {
                        isRunning: true,
                        pendingMessage: {
                            id: guid(),
                            content: [],
                            startedInference: null,
                            currentStatus: 'Starting...',
                        },
                        latestContextReadTokens: null,
                        lastUsageInputTokens: null,
                        localMessages: shouldPersistMessage
                            ? [...effectiveMessages, input.userMessage]
                            : effectiveMessages,
                        localPendingChanges: effectivePendingChanges,
                        localPendingQuestions: effectivePendingQuestions,
                    },
                },
            }));
            props.onSendMessage({
                threadID: trackedThreadID,
                message: input.content,
                persistMessage: shouldPersistMessage,
                pendingChanges: effectivePendingChanges.filter(change => change.status === 'pending'),
                pendingQuestions: effectivePendingQuestions.filter(question => question.status === 'pending'),
                useBigModel: input.useBigModel ?? false,
                callbacks: {
                    onProgress: data => {
                        updateStreamingState(rs, trackedThreadID, state => {
                            const pendingChangesCreatedAt = DateTimeObj.now().fmt();
                            const incomingChanges = data.pendingChanges
                                ?.length
                                ? data.pendingChanges.map(change => ({
                                    ...change,
                                    status: change.status ?? 'pending',
                                    createdAt: pendingChangesCreatedAt,
                                }))
                                : [];
                            const existingChanges = data.clearPendingChanges
                                ? state.localPendingChanges.filter(change => change.status !== 'pending')
                                : state.localPendingChanges;
                            const incomingQuestions = data
                                .pendingQuestions?.length
                                ? data.pendingQuestions.map(question => ({
                                    ...question,
                                    createdAt: pendingChangesCreatedAt,
                                }))
                                : [];
                            const existingQuestions = data.clearPendingQuestions
                                ? []
                                : state.localPendingQuestions;
                            const contextTokenUpdate = getContextTokenUpdate(state, data.metrics?.usage?.inputTokens);
                            return {
                                pendingMessage: {
                                    id: data.id,
                                    content: data.content,
                                    startedInference: data.metrics?.timers?.startedInference ?? null,
                                    currentStatus: data.currentStatus,
                                },
                                ...contextTokenUpdate,
                                localPendingChanges: mergeByID(existingChanges, incomingChanges),
                                localPendingQuestions: mergeByID(existingQuestions, incomingQuestions),
                            };
                        });
                    },
                    onComplete: () => {
                        rs.updateState(state => ({
                            streamingState: _.omit(state.streamingState, trackedThreadID),
                        }));
                    },
                    onError: error => {
                        updateStreamingState(rs, trackedThreadID, stream => ({
                            isRunning: false,
                            pendingMessage: null,
                            localMessages: appendAssistantErrorMessage(stream.localMessages.length > 0
                                ? stream.localMessages
                                : effectiveMessages, error),
                        }));
                    },
                },
            });
        },
        onPendingChangesChange: changes => {
            updateStreamingState(rs, stateKey, () => ({
                localPendingChanges: changes,
            }));
        },
        onPendingQuestionsChange: questions => {
            updateStreamingState(rs, stateKey, () => ({
                localPendingQuestions: questions,
            }));
        },
        onSubmitPendingQuestions: input => {
            if (!threadID || !rs.props.onSubmitQuestions) {
                return;
            }
            if (pendingQuestionSubmitInFlightRef.current?.has(threadID)) {
                return;
            }
            pendingQuestionSubmitInFlightRef.current?.add(threadID);
            const submittedQuestionIDs = new Set(input.answers.map(answer => answer.questionID));
            const questionAnswersMessage = {
                id: guid(),
                content: [
                    {
                        role: 'user',
                        timestamp: new Date().toISOString(),
                        type: 'text',
                        text: buildQuestionAnswersMessageText(input.answers),
                    },
                ],
                timestamp: new Date().toISOString(),
                avatar: null,
            };
            updateStreamingState(rs, threadID, st => ({
                localMessages: [
                    ...(st.localMessages.length > 0
                        ? st.localMessages
                        : effectiveMessages),
                    questionAnswersMessage,
                ],
                isRunning: true,
                pendingMessage: {
                    id: guid(),
                    content: [],
                    startedInference: null,
                    currentStatus: 'Starting...',
                },
                latestContextReadTokens: null,
                lastUsageInputTokens: null,
                localPendingQuestions: (st.localPendingQuestions.length > 0
                    ? st.localPendingQuestions
                    : effectivePendingQuestions).filter(question => !submittedQuestionIDs.has(question.id)),
            }));
            try {
                rs.props.onSubmitQuestions({
                    threadID,
                    answers: input.answers,
                    callbacks: {
                        onProgress: data => {
                            updateStreamingState(rs, threadID, st => {
                                const contextTokenUpdate = getContextTokenUpdate(st, data.metrics?.usage?.inputTokens);
                                return {
                                    pendingMessage: {
                                        id: data.id,
                                        content: data.content,
                                        startedInference: data.metrics?.timers?.startedInference ??
                                            null,
                                        currentStatus: data.currentStatus,
                                    },
                                    ...contextTokenUpdate,
                                };
                            });
                        },
                        onComplete: () => {
                            pendingQuestionSubmitInFlightRef.current?.delete(threadID);
                            rs.updateState(s => ({
                                streamingState: _.omit(s.streamingState, threadID),
                            }));
                        },
                        onError: error => {
                            pendingQuestionSubmitInFlightRef.current?.delete(threadID);
                            updateStreamingState(rs, threadID, st => ({
                                isRunning: false,
                                pendingMessage: null,
                                localMessages: appendAssistantErrorMessage(st.localMessages, error),
                            }));
                        },
                    },
                });
            }
            catch (error) {
                pendingQuestionSubmitInFlightRef.current?.delete(threadID);
                throw error;
            }
        },
        onRetryLastRun: () => {
            const latestUserMessage = _.findLast(streamState.localMessages.length > 0
                ? streamState.localMessages
                : effectiveMessages, message => (message.content[0]?.role ?? 'assistant') === 'user');
            if (!latestUserMessage) {
                return;
            }
            retryExistingUserMessage(latestUserMessage);
        },
        onRetryMessage: retryExistingUserMessage,
        onDeleteMessage: deleteMessageFromServer,
    };
};
const getStreamingState = (rs, threadID) => rs.state.streamingState[threadID] ?? {
    isRunning: false,
    pendingMessage: null,
    latestContextReadTokens: null,
    lastUsageInputTokens: null,
    localMessages: [],
    localPendingChanges: [],
    localPendingQuestions: [],
};
const updateStreamingState = (rs, threadID, updater) => {
    rs.updateState(state => {
        const existing = state.streamingState[threadID] ?? {
            isRunning: false,
            pendingMessage: null,
            latestContextReadTokens: null,
            lastUsageInputTokens: null,
            localMessages: [],
            localPendingChanges: [],
            localPendingQuestions: [],
        };
        return {
            streamingState: {
                ...state.streamingState,
                [threadID]: {
                    ...existing,
                    ...updater(existing),
                },
            },
        };
    });
};
const getChangeHandlers = (props) => ({
    renderers: props.changeRenderers,
    widgetToGroupCode: props.widgetToGroupCode,
    onExecuteApproved: async (input) => new Promise((resolve, reject) => {
        props.onApproveChanges({
            threadIDs: input.threadIDs,
            approvalIDs: input.approvalIDs,
            callbacks: {
                onComplete: result => {
                    resolve({
                        message: result.message,
                        errors: result.errors?.map(error => ({
                            approvalID: error.approvalID,
                            toolName: error.toolName,
                            error: error.error,
                        })),
                    });
                },
                onError: error => {
                    reject(new Error(error));
                },
            },
        });
    }),
    onReject: async (approvalIDs) => new Promise(resolve => {
        props.onRejectChanges({
            approvalIDs,
            callbacks: {
                onComplete: () => {
                    resolve();
                },
            },
        });
    }),
});
const handleContextMenu = (rs, record, e) => {
    const props = rs.props;
    e.preventDefault();
    let selectedIDs = rs.state.selectedThreadIDs;
    if (!rs.state.selectedThreadIDs.includes(record.metadata.id)) {
        rs.updateState({ selectedThreadIDs: [record.metadata.id] });
        selectedIDs = [record.metadata.id];
    }
    const realThreadIDs = selectedIDs.filter(id => id !== NEW_THREAD_ID && id !== ALL_CHANGES_ID);
    const selectedThreads = _.chain(realThreadIDs)
        .map(id => props.threads[id])
        .compact()
        .value();
    const hasArchivedThreads = selectedThreads.some(thread => thread.metadata.status === 'archived');
    const hasActiveThreads = selectedThreads.some(thread => thread.metadata.status === 'active');
    const hasSharedThreads = selectedThreads.some(thread => thread.metadata.user == null);
    const hasPrivateThreads = selectedThreads.some(thread => thread.metadata.user != null);
    const canMakePrivate = hasSharedThreads && selectedThreads.length > 0 && props.currentUserID != null;
    const executeAction = (action) => {
        const onThreadAction = props.onThreadAction;
        if (!onThreadAction || rs.state.threadActionLoading != null) {
            return;
        }
        rs.updateState({ threadActionLoading: action });
        Promise.resolve()
            .then(() => {
            onThreadAction({
                threadIDs: realThreadIDs,
                action,
                callbacks: {
                    onComplete: () => {
                        rs.updateState({ threadActionLoading: null });
                        if (action === 'archive' || action === 'unarchive') {
                            rs.updateState({ selectedThreadIDs: [NEW_THREAD_ID] });
                        }
                    },
                    onError: error => {
                        rs.updateState({ threadActionLoading: null });
                        console.error(`Failed to ${action} threads:`, error);
                    },
                },
            });
        })
            .catch(error => {
            rs.updateState({ threadActionLoading: null });
            console.error(`Failed to ${action} threads:`, error);
        });
    };
    setContextMenu({
        position: {
            x: e.clientX,
            y: e.clientY,
        },
        items: _.compact([
            props.onThreadAction && hasActiveThreads
                ? {
                    label: 'Archive',
                    icon: '/static/img/i8/color-archive.svg',
                    disabled: rs.state.threadActionLoading != null,
                    onClick: () => {
                        executeAction('archive');
                    },
                }
                : null,
            props.onThreadAction && hasArchivedThreads
                ? {
                    label: 'Unarchive',
                    icon: '/static/img/i8/ios16-refresh.svg',
                    disabled: rs.state.threadActionLoading != null,
                    onClick: () => {
                        executeAction('unarchive');
                    },
                }
                : null,
            props.onThreadAction && hasPrivateThreads
                ? {
                    label: 'Share',
                    icon: '/static/img/i8/color-collaboration.svg',
                    disabled: rs.state.threadActionLoading != null,
                    onClick: () => {
                        executeAction('share');
                    },
                }
                : null,
            props.onThreadAction && hasSharedThreads && canMakePrivate
                ? {
                    label: 'Make Private',
                    icon: '/static/img/i8/color-security-lock.svg',
                    disabled: rs.state.threadActionLoading != null,
                    onClick: () => {
                        executeAction('make-private');
                    },
                }
                : null,
        ]),
    });
};
//# sourceMappingURL=agent-ui.js.map